// Block D (2026-10-08): the CRM's modals on the v2 kit: Add contact, New board, Board settings
// (stages and columns through the existing WorkflowEditor, details, delete), and "Set up {board}",
// the template picker for a board that has no stages yet.
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { showSuccessToast } from '../../utils/toast';
import {
  Modal, Button, Field, Input, Textarea, Select, MoneyInput, Toggle, Checkbox, ChipGroup, SegmentedControl,
  ConfirmDialog, Card, EmptyState, ErrorState, Skeleton, v,
} from '../../ui';
import WorkflowEditor from '../WorkflowEditor';
import { stageColor, UNASSIGNED_ID } from './crmPipeline';

const PRESET_TAGS = ['Client', 'Press', 'Team', 'Vendor', 'Partner', 'Prospect', 'Media', 'Investor'];

export const BOARD_TYPES = [
  { value: 'NEW_BUSINESS', label: 'New business pipeline', hint: 'Track leads through to won or lost' },
  { value: 'RETENTION', label: 'Retention and renewals', hint: 'Manage returning and renewing clients' },
  { value: 'EVENT_TOUR', label: 'Event and tour pipeline', hint: 'Manage enquiries to completed events' },
  { value: 'GENERAL', label: 'General board', hint: 'A flexible pipeline for anything' },
  { value: 'CUSTOM', label: 'Custom', hint: 'Define your own stages from scratch' },
];

// ── Add contact ──────────────────────────────────────────────────────────────
// onSave(form) creates the contact, puts it on the board at the chosen stage and saves the value.
// It throws with the server's own message; the modal shows it and stays open.
// existing: contacts already in Contacts and not on this board; picking one adds that contact
// (keeping its emails, invoices and history) instead of making a second record.
export function ContactFormModal({ open, stages, defaultStageId, currencySign, existing = [], onSave, onClose }) {
  const blank = { name: '', company: '', email: '', phone: '', value: '', stageId: defaultStageId || '', tags: [], customTag: '', notes: '', isNew: false, existingId: null };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setForm({ ...blank, stageId: defaultStageId || '' }); setError(''); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));
  const valueBad = form.value.trim() !== '' && !Number.isFinite(Number(form.value.replace(/[£,\s]/g, '')));
  const picked = form.existingId ? existing.find((c) => c.id === form.existingId) : null;
  const q = form.name.trim().toLowerCase();
  const matches = !picked && q.length >= 2 ? existing.filter((c) => String(c.name || '').toLowerCase().includes(q)).slice(0, 3) : [];

  function addCustomTag() {
    const t = form.customTag.trim();
    if (!t || form.tags.includes(t)) return;
    setForm((f) => ({ ...f, tags: [...f.tags, t], customTag: '' }));
  }
  async function save() {
    if (!form.name.trim() || saving || valueBad) return;
    setSaving(true); setError('');
    try {
      await onSave({
        name: form.name.trim(), company: form.company.trim(), email: form.email.trim(), phone: form.phone.trim(),
        notes: form.notes.trim(), tags: form.tags, isNew: form.isNew, source: 'manual',
        stageId: form.stageId || null, existingId: form.existingId,
        value: form.value.trim() === '' ? null : Number(form.value.replace(/[£,\s]/g, '')),
      });
    } catch (err) {
      setError(messageFromError(err, 'The contact could not be saved. Please try again.'));
    } finally { setSaving(false); }
  }
  const stageOptions = (stages || []).filter((s) => s.id !== UNASSIGNED_ID).map((s) => ({ value: s.id, label: s.name }));
  const tagOptions = [...PRESET_TAGS, ...form.tags.filter((t) => !PRESET_TAGS.includes(t))].map((t) => ({ value: t, label: t }));

  return (
    <Modal open={open} onClose={onClose} title="Add contact" color="pastel-orange" width={520}
      footer={<>
        <Button variant="tertiary" onClick={onClose}>Cancel</Button>
        <Button variant="confirm" onClick={save} disabled={!form.name.trim() || saving || valueBad} data-testid="crm-add-save">{saving ? 'Saving…' : 'Add to board'}</Button>
      </>}>
      <div className="crm-form">
        {error && <p className="crm-problem" role="alert">{error}</p>}
        {picked ? (
          <div className="crm-section">
            <span className="ui-field__label">From Contacts</span>
            <div className="crm-row">
              <span className="crm-hero__line"><strong>{picked.name}</strong>{picked.company ? ` · ${picked.company}` : ''}{picked.email ? ` · ${picked.email}` : ''}</span>
              <Button variant="tertiary" size="sm" onClick={() => setForm((f) => ({ ...f, existingId: null }))}>Change</Button>
            </div>
          </div>
        ) : (
          <>
            <Field label="Name" required><Input autoFocus value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Full name" /></Field>
            {matches.length > 0 && (
              <div className="crm-section">
                <span className="crm-form__tip">Already in Contacts? Pick them to keep their emails and invoices linked.</span>
                <div className="crm-row">
                  {matches.map((c) => (
                    <Button key={c.id} variant="secondary" size="sm" onClick={() => setForm((f) => ({ ...f, existingId: c.id, name: c.name }))}>
                      {c.name}{c.company ? ` · ${c.company}` : ''}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <Field label="Company"><Input value={form.company} onChange={(e) => set('company', e.target.value)} placeholder="Company name" /></Field>
          </>
        )}
        <div className="crm-form__two">
          <Field label="Deal value" error={valueBad ? 'Enter an amount, like 1200' : undefined}>
            <MoneyInput currency={currencySign} value={form.value} onChange={(e) => set('value', e.target.value)} placeholder="0" />
          </Field>
          {stageOptions.length > 0 && (
            <Field label="Stage"><Select value={form.stageId} onChange={(e) => set('stageId', e.target.value)} options={stageOptions} /></Field>
          )}
        </div>
        {!picked && <div className="crm-form__two">
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="name@example.com" /></Field>
          <Field label="Phone"><Input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+44" /></Field>
        </div>}
        {!picked && <div className="crm-section">
          <span className="ui-field__label">Tags</span>
          <ChipGroup multi label="Tags" options={tagOptions} value={form.tags} onChange={(tags) => set('tags', tags)} scroll={false} />
          <div className="crm-row">
            <Input aria-label="Add a tag of your own" value={form.customTag} placeholder="Add a tag" style={{ flex: '1 1 160px', width: 'auto' }}
              onChange={(e) => set('customTag', e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }} />
            <Button variant="secondary" size="sm" onClick={addCustomTag} disabled={!form.customTag.trim()}>Add tag</Button>
          </div>
        </div>}
        {!picked && <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Anything worth remembering" /></Field>}
        {!picked && <Checkbox label="Mark as a new contact" checked={form.isNew} onChange={(c) => set('isNew', c)} />}
        {!picked && matches.length === 0 && <p className="crm-form__tip">Tip: type a name already in Contacts to pick them, so their emails and invoices stay linked.</p>}
      </div>
    </Modal>
  );
}

// ── New board ────────────────────────────────────────────────────────────────
// onSave({ name, description, boardType, workflowDraft }) makes the board in ONE request: with the
// customised stages when "Customise workflow" is on, else from the type's template on the server.
export function CreateBoardModal({ open, onSave, onClose }) {
  const [name, setName] = useState('');
  const [boardType, setBoardType] = useState('NEW_BUSINESS');
  const [description, setDescription] = useState('');
  const [customise, setCustomise] = useState(false);
  const [draft, setDraft] = useState({ stages: [], columns: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setName(''); setBoardType('NEW_BUSINESS'); setDescription(''); setCustomise(false); setDraft({ stages: [], columns: [] }); setError(''); } }, [open]);
  const onDraft = useCallback((d) => setDraft(d), []);

  async function submit() {
    if (!name.trim()) { setError('A board needs a name.'); return; }
    setSaving(true); setError('');
    try {
      await onSave({ name: name.trim(), description: description.trim(), boardType, workflowDraft: customise ? draft : null });
      showSuccessToast(`${name.trim()} created.`);
    } catch (err) {
      setError(messageFromError(err, 'The board could not be created.'));
    } finally { setSaving(false); }
  }
  const type = BOARD_TYPES.find((t) => t.value === boardType);
  return (
    <Modal open={open} onClose={onClose} title="New board" color="pastel-green" width={640}
      footer={<>
        <Button variant="tertiary" onClick={onClose}>Cancel</Button>
        <Button variant="confirm" onClick={submit} disabled={saving || !name.trim()}>{saving ? 'Creating…' : 'Create board'}</Button>
      </>}>
      <div className="crm-form">
        {error && <p className="crm-problem" role="alert">{error}</p>}
        <Field label="Board name" required><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="New artist pipeline" /></Field>
        <Field label="Board type" hint={type?.hint}>
          <Select value={boardType} onChange={(e) => { setBoardType(e.target.value); setCustomise(e.target.value === 'CUSTOM'); }} options={BOARD_TYPES} />
        </Field>
        <Field label="Description (optional)"><Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this board for?" /></Field>
        {boardType !== 'CUSTOM' && (
          <Toggle label="Customise the stages" checked={customise} onChange={setCustomise} />
        )}
        {!customise && <p className="crm-form__tip">The stages of the chosen type are used. You can change them any time in Edit stages.</p>}
        {customise && boardType === 'CUSTOM' && <p className="crm-form__tip">Add your stages below, or create the board now and pick a template on it.</p>}
        {customise && <WorkflowEditor mode="crm-board" entityId={null} showColumns showAutomations onChange={onDraft} />}
      </div>
    </Modal>
  );
}

// ── Board settings ───────────────────────────────────────────────────────────
export function BoardSettingsModal({ open, board, canDelete, onClose, onSaved, onDelete }) {
  const [tab, setTab] = useState('stages');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { if (open) { setTab('stages'); setName(board?.name || ''); setDescription(board?.description || ''); setError(''); } }, [open, board?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveDetails() {
    setSaving(true); setError('');
    try {
      await api(`/api/crm-boards/${board.id}`, { method: 'PUT', body: JSON.stringify({ name, description }) });
      await onSaved({ close: true });
    } catch (err) { setError(messageFromError(err, 'The board could not be saved.')); }
    finally { setSaving(false); }
  }
  if (!board) return null;
  return (
    <>
      <Modal open={open && !confirm} onClose={onClose} title={`Edit ${board.name}`} color="pastel-green" width={720}
        footer={<Button variant="tertiary" onClick={onClose}>Done</Button>}>
        <SegmentedControl label="Settings" value={tab} onChange={setTab}
          options={[{ value: 'stages', label: 'Stages and columns' }, { value: 'details', label: 'Board details' }]} />
        {tab === 'stages' && (
          <WorkflowEditor mode="crm-board" entityId={board.id} readOnly={false} showColumns showAutomations showSaveAsTemplate
            onSaved={() => onSaved({ close: false })} />
        )}
        {tab === 'details' && (
          <div className="crm-form">
            {error && <p className="crm-problem" role="alert">{error}</p>}
            <Field label="Board name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Description"><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
            <div><Button variant="confirm" onClick={saveDetails} disabled={saving || !name.trim()}>{saving ? 'Saving…' : 'Save changes'}</Button></div>
            <div className="crm-section">
              <span className="crm-label">Delete this board</span>
              <p className="crm-muted">{canDelete ? 'The board and the cards on it go. The contacts stay in Contacts.' : 'A business keeps at least one board, so the only board cannot be deleted.'}</p>
              <div><Button variant="destructive" onClick={() => setConfirm(true)} disabled={!canDelete}>Delete board</Button></div>
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog open={confirm} onClose={() => setConfirm(false)} itemType="board" itemName={board.name}
        warningText={`Delete ${board.name} and every card on it? The contacts stay in Contacts. You can undo for a few seconds.`}
        onConfirm={() => { onDelete(board); onClose(); }} />
    </>
  );
}

// ── Set up {board}: a board with no stages ───────────────────────────────────
export function BoardSetup({ board, canManage, onApplied }) {
  const [state, setState] = useState({ loading: true, error: null, templates: [] });
  const [applying, setApplying] = useState(null);
  const load = useCallback(async () => {
    setState({ loading: true, error: null, templates: [] });
    try {
      const res = await api('/api/workflow-templates?type=CRM');
      const d = await res.json();
      setState({ loading: false, error: null, templates: d.templates || [] });   // Aidey's first, then your own
    } catch (err) { setState({ loading: false, error: messageFromError(err, 'The templates did not load.'), templates: [] }); }
  }, []);
  useEffect(() => { if (canManage) load(); }, [canManage, load]);

  async function apply(t) {
    setApplying(t.id);
    try {
      await api(`/api/crm-boards/${board.id}/apply-template`, { method: 'POST', body: JSON.stringify({ templateId: t.id }) });
      await onApplied(t);
    } catch (err) {
      setState((s) => ({ ...s, error: messageFromError(err, 'The stages could not be added.') }));
    } finally { setApplying(null); }
  }

  if (!canManage) {
    return (
      <EmptyState icon="🧭" title={`${board.name} has no stages yet`}>
        The owner or an admin sets up a board's stages. Once they have, your contacts appear here.
      </EmptyState>
    );
  }
  return (
    <Card color="pastel-periwinkle" label={`Set up ${board.name}`} meta="Choose stages" lift={false}>
      <div className="crm-form">
        <div className="crm-setup__intro">
          <h2 className="crm-setup__title">Start from a template</h2>
          <p className="crm-setup__text">Pick the stages your clients move through. You can rename, reorder or add stages any time.</p>
        </div>
        {state.loading && <Skeleton lines={3} label="Loading templates" />}
        {state.error && <ErrorState message={state.error} onRetry={load} />}
        {!state.loading && !state.error && state.templates.length === 0 && (
          <EmptyState title="No templates found">Add stages yourself in Edit stages.</EmptyState>
        )}
        {state.templates.length > 0 && (
          <div className="crm-setup__grid">
            {state.templates.map((t) => (
              <button key={t.id} type="button" className="crm-tpl" disabled={!!applying} onClick={() => apply(t)} aria-busy={applying === t.id || undefined}>
                <span className="crm-tpl__name">{applying === t.id ? 'Adding stages…' : t.name}</span>
                {t.description && <span className="crm-tpl__desc">{t.description}</span>}
                <span className="crm-tpl__stages">
                  {(t.stages || []).map((s, i) => <span key={s.id || s.name} className="crm-pill" style={{ '--ui-bg': v(stageColor(s, i)) }}>{s.name}</span>)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
